import { Link } from 'react-router';
import { formatMoney, type InstrumentHoldings } from '@budget/core';
import { useAuth } from '../features/auth/auth-context.js';
import { usePortfolio, type Portfolio } from '../features/portfolio/use-holdings.js';

/**
 * The per-broker breakdown.
 *
 * This is the view the spreadsheet could not produce: it knew the totals but
 * not which custodian held what, because both sides were kept by hand. Here
 * the numbers come from the brokers — IBKR as a daily position snapshot,
 * Lightyear folded from its transaction export — and the split falls out.
 */
export function PortfolioPage() {
  const { status } = useAuth();
  const { portfolio, isPending, isError } = usePortfolio({
    enabled: status === 'authenticated',
  });

  if (isPending) return <p className="muted">Betöltés…</p>;
  if (isError || !portfolio) {
    return <p className="alert alert--error">Nem sikerült betölteni a portfóliót.</p>;
  }

  if (portfolio.byInstrument.length === 0) {
    return (
      <section className="card">
        <h1 className="card__title">Portfólió</h1>
        <p className="muted">
          Még nincs állomány. Kösd be az IBKR-t, vagy{' '}
          <Link to="/import">importáld a Lightyear exportot</Link>.
        </p>
      </section>
    );
  }

  return (
    <div className="stack">
      <h1 className="page-title">
        Portfólió
        <Link className="page-title__action" to="/import">
          Lightyear import
        </Link>
      </h1>
      {portfolio.byInstrument.map((instrument) => (
        <InstrumentCard key={instrument.instrumentId} group={instrument} portfolio={portfolio} />
      ))}
    </div>
  );
}

function InstrumentCard({ group, portfolio }: { group: InstrumentHoldings; portfolio: Portfolio }) {
  const instrument = portfolio.instruments.get(group.instrumentId);

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="instrument-title">
          <span className="instrument-title__ticker">{instrument?.ticker ?? 'Ismeretlen'}</span>
          {instrument?.name ? ` ${instrument.name}` : ''}
        </h2>
        <span className="badge">{instrument?.assetClass ?? '—'}</span>
      </div>

      <p className="figure">
        {group.totalQuantity.toFixed()} <span className="figure__unit">db</span>
      </p>
      <p className="muted">
        {group.totalCostBasis
          ? `Bekerülési érték: ${formatMoney(group.totalCostBasis)}`
          : 'Bekerülési érték: részben ismeretlen'}
      </p>

      <table className="table">
        <thead>
          <tr>
            <th scope="col">Hol</th>
            <th scope="col">Darab</th>
            <th scope="col">Bekerülés</th>
            <th scope="col">Forrás</th>
          </tr>
        </thead>
        <tbody>
          {group.byAccount.map((holding) => {
            const account = portfolio.accounts.get(holding.accountId);
            return (
              <tr key={holding.accountId}>
                <td>{account?.name ?? holding.accountId}</td>
                <td className="numeric">{holding.quantity.toFixed()}</td>
                <td className="numeric">
                  {holding.costBasis ? formatMoney(holding.costBasis) : '—'}
                </td>
                <td>
                  {holding.source === 'snapshot' ? (
                    <span title={`A bróker jelentette, ${holding.asOf}`}>bróker</span>
                  ) : (
                    <span title="Tranzakciókból számolva">tranzakciók</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {group.byAccount.length > 1 ? (
        <p className="muted">Ugyanez az instrumentum {group.byAccount.length} helyen van.</p>
      ) : null}
    </section>
  );
}
